import { createFileRoute, Link, Navigate, redirect, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatRef, parseRef, streetLine } from '@promanage/core';
import { Button, Ref } from '@promanage/ui';
import { TopBar } from '../components/TopBar';
import { useUnsavedChanges } from '../components/UnsavedChanges';
import { PropertyDetailsForm } from '../features/property/PropertyDetailsForm';
import {
  toDetailValues,
  toDetailsInput,
  type PropertyDetailValues,
} from '../features/property/details';
import { trpc } from '../lib/trpc';

/**
 * Edit a property's own description — the address, what the building is, its Healthy Homes
 * standing, and the notes about it.
 *
 * OWNERSHIP, THE FEE AND THE MANAGEMENT START ARE NOT EDITED HERE, and each is refused for its
 * own reason. The ownership split is time-sliced and needs a trust role, because changing it
 * redirects money that is already flowing to a different person out of a trust account. The fee
 * has its own panel on the record, where it is checked against the agency's GST registration.
 * The management start is also the date every ownership interest runs from, so moving it here
 * would leave the property claiming one start date and its owners another. All three are
 * changed from the record, with their own dates and their own guards.
 *
 * Unlike the record page this route renders no "not found" screens of its own: anything that
 * does not resolve to a property is handed to `/properties/$ref`, which already explains all
 * three ways a reference can fail. One place to get that wording right.
 */
export const Route = createFileRoute('/properties/$ref/edit')({
  beforeLoad: ({ params }) => {
    const parsed = parseRef(params.ref);

    // Normalise first — `pr3` edits the same record as PR000003.
    if (parsed?.entity === 'property') {
      const canonical = formatRef('property', parsed.sequence);
      // Loop-safe: formatRef of a parsed canonical ref is that same string.
      if (canonical !== params.ref) {
        throw redirect({
          to: '/properties/$ref/edit',
          params: { ref: canonical },
          replace: true,
        });
      }
      return;
    }

    // Only the SHAPE is checked here. Whether the record exists is the server's answer, and
    // `beforeLoad` cannot wait on it without blocking the route transition on a fetch — so a
    // ref that resolves to nothing is handled below, by sending it to the record page.
    //
    // Terminates: the record page only redirects a ref that parses AND is not canonical.
    throw redirect({ to: '/properties/$ref', params: { ref: params.ref }, replace: true });
  },
  component: EditProperty,
});

function EditProperty() {
  const { ref } = Route.useParams();
  const navigate = useNavigate();
  const unsaved = useUnsavedChanges();
  const queryClient = useQueryClient();

  const parsed = parseRef(ref);
  // `beforeLoad` has already sent anything that is not a property ref to the record page, so
  // this only stands in for the render before that redirect lands.
  const refSeq = parsed?.entity === 'property' ? parsed.sequence : 1;

  const property = useQuery(
    trpc.property.get.queryOptions({ refSeq }, { staleTime: 10_000, retry: false }),
  );

  const update = useMutation(
    trpc.property.update.mutationOptions({
      meta: { errorTitle: 'Could not save those changes' },
      onSuccess() {
        unsaved.saved();
        void queryClient.invalidateQueries({ queryKey: trpc.property.get.queryKey() });
        void queryClient.invalidateQueries({ queryKey: trpc.property.list.queryKey() });
        // The address is how a property is named everywhere it appears — the portfolio tree,
        // the bill it was raised against, the tenancy that sits on it.
        void queryClient.invalidateQueries({ queryKey: trpc.property.portfolio.queryKey() });
        void navigate({
          to: '/properties/$ref',
          params: { ref: formatRef('property', refSeq) },
        });
      },
    }),
  );

  if (property.isPending) {
    return (
      <div className="mx-auto max-w-[var(--content-max)] pb-4">
        <TopBar title="Edit property" subtitle={formatRef('property', refSeq)} />
      </div>
    );
  }

  // A ref nobody has issued, or another agency's. The record page owns that wording, and its
  // three separate messages are better than a fourth written here.
  if (property.error || !property.data) {
    return (
      <Navigate to="/properties/$ref" params={{ ref: formatRef('property', refSeq) }} replace />
    );
  }

  const record = property.data;

  const save = (values: PropertyDetailValues) =>
    update.mutate({ propertyId: record.id, values: toDetailsInput(values) });

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title="Edit property"
        subtitle={`${formatRef('property', record.refSeq)} · ${streetLine(record)}`}
      />

      <div className="contents" onChange={unsaved.touch}>
        <PropertyDetailsForm
          initial={toDetailValues(record)}
          submitLabel="Save changes"
          onSubmit={save}
          pending={update.isPending}
          cancel={
            <Link to="/properties/$ref" params={{ ref: formatRef('property', record.refSeq) }}>
              <Button type="button" variant="secondary">
                Cancel
              </Button>
            </Link>
          }
          footnote={
            <>
              Editing <Ref entity="property" sequence={record.refSeq} /> — the owners, the
              management fee and the management start date are changed from the record, where each
              carries its own dates.
            </>
          }
        />
      </div>
    </div>
  );
}
